import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

interface OfflineContextType {
  isOnline: boolean
  isOfflineMode: boolean
  pendingSyncCount: number
  syncData: () => Promise<void>
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined)

export const useOffline = () => {
  const context = useContext(OfflineContext)
  if (context === undefined) {
    throw new Error('useOffline must be used within an OfflineProvider')
  }
  return context
}

interface OfflineProviderProps {
  children: ReactNode
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isOfflineMode, setIsOfflineMode] = useState(false)
  const [pendingSyncCount, setPendingSyncCount] = useState(0)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      setIsOfflineMode(false)
      // Trigger sync when coming back online
      syncData()
    }

    const handleOffline = () => {
      setIsOnline(false)
      setIsOfflineMode(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check for pending sync items on load
    checkPendingSync()

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const checkPendingSync = async () => {
    try {
      const db = await openIndexedDB()
      const transaction = db.transaction(['pending_sync'], 'readonly')
      const store = transaction.objectStore('pending_sync')
      
      // Wrap IDBRequest in a Promise
      const count = await new Promise<number>((resolve, reject) => {
        const request = store.count()
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      
      setPendingSyncCount(count)
    } catch (error) {
      console.error('Error checking pending sync:', error)
    }
  }

  const openIndexedDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('POSOfflineDB', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create object stores for offline data
        if (!db.objectStoreNames.contains('orders')) {
          db.createObjectStore('orders', { keyPath: 'id', autoIncrement: true })
        }
        
        if (!db.objectStoreNames.contains('products')) {
          db.createObjectStore('products', { keyPath: 'id' })
        }
        
        if (!db.objectStoreNames.contains('customers')) {
          db.createObjectStore('customers', { keyPath: 'id' })
        }
        
        if (!db.objectStoreNames.contains('pending_sync')) {
          db.createObjectStore('pending_sync', { keyPath: 'id', autoIncrement: true })
        }
      }
    })
  }

  const syncData = async () => {
    if (!isOnline) return

    try {
      const db = await openIndexedDB()
      const transaction = db.transaction(['pending_sync'], 'readonly')
      const store = transaction.objectStore('pending_sync')
      const request = store.getAll()
      const pendingItems = await new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })

      // Process each pending item
      for (const item of pendingItems as any[]) {
        try {
          // Sync to Supabase based on item type
          await syncItemToSupabase(item)
          
          // Remove from pending sync
          const deleteTransaction = db.transaction(['pending_sync'], 'readwrite')
          const deleteStore = deleteTransaction.objectStore('pending_sync')
          await deleteStore.delete(item.id)
        } catch (error) {
          console.error('Error syncing item:', error)
        }
      }

      setPendingSyncCount(0)
    } catch (error) {
      console.error('Error during sync:', error)
    }
  }

  const syncItemToSupabase = async (_item: any) => {
    // This would contain the actual Supabase sync logic
    // For now, just log the item

  }

  return (
    <OfflineContext.Provider value={{
      isOnline,
      isOfflineMode,
      pendingSyncCount,
      syncData
    }}>
      {children}
    </OfflineContext.Provider>
  )
}
